import {
  CommonApiResponse,
  companyDetailCreateResponse,
  emailVerifiedOTPSendResponse,
  ForgotPasswordRequestOtpResponse,
  LoginResponse,
  SubscriptionResponse,
  userCreateResponse,
  UserTypeResponse,
  Country,
  ApiResponse,
  ForgotOTPCheckResponse,
} from "../Controllers/Types/ApiResponse";
import { mapSubscriptions } from "../Controllers/Types/SubscriptionInterface";
import { mapUserTypes } from "../Controllers/Types/UserTypeInterface";
import apiClient from "./apiClient";
import { baseURL } from "./ApiConstant";

export const loginService = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, body: data };
    } else if (response.status === 404) {
      return { status: false, error: data["massage"] };
    } else {
      return { status: false, error: "Login failed" };
    }
  } catch (error) {
    throw error;
  }
};

export const forgotPasswordRequestOtp = async (
  email: string
): Promise<ForgotPasswordRequestOtpResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/forget-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, body: data, statusCode: 200 };
    } else if (response.status === 404) {
      return { status: false, error: data["massage"], statusCode: 404 };
    } else if (response.status === 422) {
      return {
        status: false,
        error: data["massage"],
        statusCode: 422,
        errors: data["errors"],
      };
    } else {
      return { status: false, error: "An error occurred in the forgotten password request.", statusCode: 500 };
    }
  } catch (error) {
    throw error;
  }
};

export const forgotPasswordOtpCheck = async (
  email: string,
  otp: string,
): Promise<ForgotOTPCheckResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/forget-otp-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email, otp }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return {
        otp_verified: data?.otp_verified ?? true,
        massage: data?.massage ?? null,
        statusCode: 200,
      };
    } else if (response.status === 404) {
      return {
        otp_verified: false,
        massage: data?.massage ?? "Email not found",
        statusCode: 404,
      };
    } else if (response.status === 422) {
      return {
        otp_verified: false,
        massage: data?.massage ?? "Validation failed",
        errors: data?.errors ?? [],
        statusCode: 422,
      };
    } else {
      return {
        otp_verified: false,
        massage: "OTP verification failed",
        statusCode: response.status,
      };
    }
  } catch (error: any) {
    return {
      otp_verified: false,
      massage: error?.massage ?? "Unexpected error",
      errorInfo: [error],
      statusCode: 500,
    };
  }
};

export const forgotPasswordReset = async (
  user_id: string,
  password: string
): Promise<ForgotPasswordRequestOtpResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/password-change`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ user_id, password }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, body: data, statusCode: 200 };
    } else if (response.status === 404) {
      return { status: false, error: data["massage"], statusCode: 404 };
    } else if (response.status === 422) {
      return {
        status: false,
        error: data["massage"],
        statusCode: 422,
        errors: data["errors"],
      };
    } else {
      return { status: false, error: data["massage"], statusCode: 500 };
    }
  } catch (error) {
    throw error;
  }
};

export const emailVerifiedOTPSend = async (
  email: string
): Promise<emailVerifiedOTPSendResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/email-verified`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, statusCode: 200, OTP: data["OTP"] };
    } else if (response.status === 422) {
      return {
        status: false,
        statusCode: 422,
        error: data["massage"],
        errors: data["errors"],
      };
    } else {
      return { status: false, statusCode: 500, error: "OTP sending failed." };
    }
  } catch (error) {
    throw error;
  }
};

export const otpVerification = async (
  email: string, otp: number
): Promise<emailVerifiedOTPSendResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/email-otp-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        'Accept': "application/json",
      },
      body: JSON.stringify({ email, otp }),
    });

    const data = await response.json();

    if (response.status === 200) {
      if (data['email_verified']) {
        return { status: true, statusCode: 200, OTP: data["OTP"] };
      } else {
        return { status: false, statusCode: 200, error: data["massage"], errors: data["errors"] };
      }

    } else if (response.status === 422) {
      return { status: false, statusCode: 422, error: data["massage"], errors: data["errors"] };
    } else {
      return { status: false, statusCode: 500, error: "OTP verification failed." };
    }
  } catch (error) {
    return { status: false, statusCode: 500, error: "OTP verification failed." };
  }
};

export const userCreate = async (
  user_type_id: number,
  country_id: number,
  first_name: string,
  last_name: string,
  email: string,
  phone: string,
  password: string,
  confirm_password: string
): Promise<userCreateResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        user_type_id,
        country_id,
        first_name,
        last_name,
        email,
        phone,
        password,
        confirm_password,
      }),
    });

    const data = await response.json();


    if (response.status === 200) {
      return { status: true, statusCode: 200, user_id: data["user_id"], token: data["token"] };
    } else if (response.status === 422) {
      return {
        status: false,
        statusCode: 422,
        error: data["massage"],
        errors: data["errors"],
      };
    } else {
      return { status: false, statusCode: 500, error: "User creation failed." };
    }
  } catch (error) {
    throw error;
  }
};

export const companyDetailCreate = async (user_id: number, company_name: string, company_regno: string, company_email: string, company_phone: string, company_address: string, post_code: string, city: string): Promise<companyDetailCreateResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user/add-company-details`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        user_id,
        company_name,
        company_regno,
        company_email,
        company_phone,
        company_address,
        post_code,
        city,
      }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, statusCode: 200, user_id: data["user_id"] };
    } else if (response.status === 422) {
      return {
        status: false,
        statusCode: 422,
        error: data["massage"],
        errors: data["errors"],
      };
    } else {
      return { status: false, statusCode: 500, error: "User creation failed." };
    }
  } catch (error) {
    throw error;
  }
};

export const fetchUserTYpes = async (): Promise<UserTypeResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/user-type`);

    if (response.status === 200) {
      const data = await response.json();
      const userTypes = mapUserTypes(data["data"]);

      return { status: true, body: userTypes };
    } else {
      return { status: false, error: "Error fetching user types" };
    }
  } catch (err) {
    //throw error;
    return { status: false, error: err!.toString() || "" };
  }
};

export const fetchCountries = async (): Promise<Country[]> => {
  try {
    const response = await fetch(`${baseURL}/api/country-list`);
    if (!response.ok) {
      throw new Error("Failed to fetch country list");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching countries:", error);
    throw error;
  }
};

export const fetchSubscription = async (): Promise<SubscriptionResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/subscription`);

    if (response.status === 200) {
      const data = await response.json();
      const userTypes = mapSubscriptions(data["data"]);

      return { status: true, body: userTypes };
    } else {
      return { status: false, error: "Error fetching subscriptions" };
    }
  } catch (err) {
    return { status: false, error: err!.toString() || "" };
  }
};

export const applyPromocode = async (code: string,): Promise<ApiResponse> => {
  try {
    const data = {
      code: code,

    };

    const response = await apiClient.post(`/api/promocode/apply`, data);

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return { status: false, error: "failed" };
    }
  } catch (error: any) {
    return {
      status: false,
      error: "An error occurred. Please try again later.",
    };
  }
};

export const addBillingAddress = async (user_id: number, first_name: string, last_name: string, address_line_1: string, address_line_2: string, city: string, postal_code: string): Promise<ApiResponse> => {
  try {
    const data = {
      user_id: user_id,
      first_name: first_name,
      last_name: last_name,
      address_line_1: address_line_1,
      address_line_2: address_line_2,
      city: city,
      postal_code: postal_code,

    };

    const response = await apiClient.post(`/api/billing-address/add`, data);

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return { status: false, error: "failed" };
    }
  } catch (error: any) {
    return {
      status: false,
      error: "An error occurred. Please try again later.",
    };
  }
};

export const addPayment = async (user_id: number, payment_type_id: number, subscription_id: number, sub_total: number, discount: number, tax: number, total: number, license_count: number, payment_id: string): Promise<ApiResponse> => {
  try {
    const data = {
      user_id: user_id,
      payment_type_id: payment_type_id,
      subscription_id: subscription_id,
      sub_total: sub_total,
      discount: discount,
      tax: tax,
      total: total,
      license_count: license_count,
      is_limited: 'Limited',
      payment_id: payment_id

    };

    const response = await apiClient.post(`/api/payment/add`, data);

    if (response.status === 200) {
      return { status: true, data: response.data };
    } else {
      return { status: false, error: "failed" };
    }
  } catch (error: any) {
    return {
      status: false,
      error: "An error occurred. Please try again later.",
    };
  }
};

export const getAllFaqApi = async () => {
  try {
    const response = await apiClient.get(`/api/faq`);

    if (response.status === 200) {
      return { status: true, body: response.data };
    } else {
      return { status: false, error: "failed" };
    }
  } catch (error: any) {
    return {
      status: false,
      error: "An error occurred. Please try again later.",
    };
  }
};

export const addContactUsApi = async (
  name: string,
  email: string,
  massage: string
): Promise<CommonApiResponse> => {
  try {
    const USERID: string = localStorage.getItem("user_id") || "0";

    const data = {
      user_id: Number(USERID),
      name: name,
      email: email,
      massage: massage,
    };

    const response = await apiClient.post(`/api/contact-us/email-send`, data);

    if (response.status === 200 || response.status === 201) {
      return { status: true, massage: response.data };
    } else if (response.status === 422) {
      return { status: false, error: response.data["message"] };
    } else {
      return { status: false, error: "Error sending contact message." };
    }
  } catch (error: any) {
    return {
      status: false,
      error: "An error occurred. Please try again later.",
    };
  }
};

export const createPaymentIntent = async (amountVal: number) => {
  try {
    const amount = amountVal;

    const response = await fetch(
      `http://localhost:4006/v1/api/stripe/createPaymentIntent`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ amount }),
      }
    );

    if (response.status === 200) {
      const data = await response.json();

      return { status: true, body: data.clientSecret };
    } else {
      return { status: false, error: "CreatePaymentIntent failed" };
    }
  } catch (err) {
    return { status: false, error: err!.toString() || "" };
  }
};



/* ********************  Set User type for contact create user ********************** */
export const newUserUserTypeAdd = async (email: string, user_type_id: number): Promise<CommonApiResponse> => {
  try {
    const response = await fetch(`${baseURL}/api/new-user-usertype-add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ email, user_type_id }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, statusCode: 200 };
    } else if (response.status === 401) {
      return { status: false, statusCode: 401, massage: "Unauthorized" };
    } else if (response.status === 422) {
      return { status: false, statusCode: 422, massage: "Body error", errors: data['errors'] };
    } else if (response.status === 404) {
      return { status: false, statusCode: 404, massage: "Not found" };
    } else {
      return { status: false, statusCode: 500, massage: "Error" };
    }

  } catch (error) {
    throw error;
  }
};
/* ********************  Set User type for contact create user ********************** */


/* Delete account */
export const deleteAccountOTPSend = async (): Promise<CommonApiResponse> => {
  try {
    const token = localStorage.getItem('token') || '';
    const FULL_TOKEN = `Bearer ${token}`;
    const response = await fetch(`${baseURL}/api/user/delete-account-otp-send`, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
        'Accept': "application/json",
        'Authorization': FULL_TOKEN
      },
      body: JSON.stringify({}),
    });

    const data = await response.json();

    if (response.status === 200) {
      return { status: true, statusCode: 200 };
    } else if (response.status === 422) {
      return {
        status: false,
        statusCode: 422,
        error: data["massage"],
        errors: data["errors"],
      };
    } else {
      return { status: false, statusCode: 500, error: "OTP sending failed." };
    }
  } catch (error) {
    throw error;
  }
};

export const deleteAccountOTPVerify = async (
  otp: string | number
): Promise<CommonApiResponse> => {
  try {
    const token = localStorage.getItem("token") || "";
    const FULL_TOKEN = `Bearer ${token}`;

    const response = await fetch(`${baseURL}/api/user/delete-account-otp-check`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: FULL_TOKEN,
      },
      body: JSON.stringify({ otp: String(otp) }),
    });

    const data = await response.json();

    if (response.status === 200) {
      return {
        status: true,
        massage: data?.massage ?? "OTP verified successfully",
        statusCode: 200,
      };
    } else if (response.status === 404) {
      return {
        status: false,
        massage: data?.massage ?? "Email not found",
        statusCode: 404,
      };
    } else if (response.status === 422) {
      return {
        status: false,
        massage: data?.massage ?? "Validation failed",
        errors: data?.errors ?? [],
        errorInfo: data?.errorInfo ?? [],
        statusCode: 422,
      };
    } else {
      return {
        status: false,
        massage: data?.massage ?? "OTP verification failed",
        statusCode: response.status,
      };
    }
  } catch (error: any) {
    return {
      status: false,
      massage: error?.massage ?? "Unexpected error",
      errors: [error],
      statusCode: 500,
    };
  }
};